import { useCallback, useEffect, useRef, useState } from "react";
import { clearAuth, SESSION_ENDED } from "@/lib/api";

/**
 * Gate for the Super Admin, Tenant Admin and Seller consoles. Nothing inside a
 * console is rendered, and so none of its pages request data, until the saved
 * login has been confirmed with the server for that console.
 *
 *   verify()     resolves to something truthy when the login is valid here (it is
 *                returned as `data`), to false when it is not; rejects if the
 *                request itself fails
 *   onRefused()  no valid login: the caller navigates to its login page
 *   key          the check runs again when this changes (the tenant slug)
 *
 * status: "checking" | "ready" | "unreachable". A server that cannot be reached
 * does not sign anyone out: the console offers retry() instead.
 */
export function useVerifiedSession(verify, onRefused, key) {
  const [state, setState] = useState({ status: "checking", data: null });
  const [attempt, setAttempt] = useState(0);
  const fns = useRef({ verify, onRefused });
  fns.current = { verify, onRefused };

  useEffect(() => {
    let current = true;
    const refuse = () => {
      if (!current) return;
      current = false;
      clearAuth();
      fns.current.onRefused();
    };
    setState({ status: "checking", data: null });
    fns.current.verify().then((data) => {
      if (!current) return;
      if (data) setState({ status: "ready", data }); else refuse();
    }).catch((err) => {
      const code = err?.response?.status;
      if (code && code < 500) refuse();
      else if (current) setState({ status: "unreachable", data: null });
    });
    // A login that stops being valid while the console is open (expired, account
    // disabled) is reported by the API client.
    window.addEventListener(SESSION_ENDED, refuse);
    return () => { current = false; window.removeEventListener(SESSION_ENDED, refuse); };
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
